<template>
  <div>
    <b-form-group label="Type" label-for="type">
      <b-form-select
        id="type"
        v-model="type"
        :options="types"
        :disabled="!enabled"
        required
        size="sm"
        @keyup="handleKey"
      ></b-form-select>
    </b-form-group>
    <b-form-group
      v-for="field in fields"
      :key="field.name"
      :label="label(field.name)"
      label-for="field.name"
    >
      <b-form-input
        v-model="adapter[field.name]"
        :id="field.name"
        :type="field.type"
        :placeholder="field.placeholder"
        :disabled="!enabled"
        size="sm"
        @keyup="handleKey"
      ></b-form-input>
    </b-form-group>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
// NOTE: Let's hope this doesn't result in the whole lodash package
import startCase from 'lodash/startCase';
import isEqual from 'lodash/isEqual';
import { getAdapterDefaultValues } from '@aison/migrator/lib/utils';
import { adapters } from '@aison/migrator';

const baseFields = [
  { name: 'port', type: 'number', order: 2 },
  { name: 'database', type: 'text', order: 3 },
  { name: 'user', type: 'text', order: 4 },
  { name: 'password', type: 'password', order: 5 },
];

const hostField = { name: 'host', type: 'text', order: 1 };
const serverField = { name: 'server', type: 'text', order: 1 };
const caField = { name: 'ca', type: 'text', order: 100 };

const TYPES = {
  POSTGRESQL: 'postgresql',
  MYSQL: 'mysql',
  ORACLE: 'oracle',
  MONGODB: 'mongodb',
  MSSQL: 'mssql',
  DB2: 'db2',
  CSV: 'csv',
  COBOL: 'cobol',
};

const csvFields = [
  { name: 'filepath', type: 'text', order: 1, placeholder: '/path/to/file.csv' },
  { name: 'delimiter', type: 'text', order: 2, placeholder: ',' },
  { name: 'encoding', type: 'text', order: 3, placeholder: 'utf8' },
  { name: 'hasHeader', type: 'text', order: 4, placeholder: 'true' },
];

const cobolFields = [
  { name: 'filepath', type: 'text', order: 1, placeholder: '/path/to/data.dat' },
  { name: 'copybookPath', type: 'text', order: 2, placeholder: '/path/to/schema.cpy' },
  { name: 'encoding', type: 'text', order: 3, placeholder: 'ebcdic' },
  { name: 'codepage', type: 'text', order: 4, placeholder: 'cp037' },
  { name: 'recordLength', type: 'number', order: 5, placeholder: 'auto' },
];

const fields = Object.fromEntries(
  [
    [TYPES.POSTGRESQL, [...baseFields, hostField, caField]],
    [TYPES.MYSQL, [...baseFields, hostField, caField]],
    [TYPES.MSSQL, [...baseFields, serverField]],
    [TYPES.MONGODB, [...baseFields, hostField]],
    [TYPES.ORACLE, [...baseFields, hostField]],
    [TYPES.DB2, [...baseFields, hostField]],
    [TYPES.CSV, csvFields],
    [TYPES.COBOL, cobolFields],
  ].map(([type, fields]) => {
    const defaultValues = getAdapterDefaultValues(type as adapters.AdapterType);
    return [
      type,
      (fields as any[])
        .sort((a, b) => a.order - b.order)
        .map(field => {
          if (defaultValues[field.name]) {
            return {
              ...field,
              placeholder: defaultValues[field.name] + '',
            };
          }
          return field;
        }),
    ];
  })
);

export type DatabaseOptions = {
  type: string;
  adapter: adapters.InputAdapterOptions;
};

export default defineComponent({
  name: 'DatabaseForm',
  components: {},
  props: {
    value: {
      type: Object as () => DatabaseOptions,
      default: () => ({
        type: undefined,
        adapter: {},
      }),
    },
    enabled: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      type: this.value?.type,
      adapter: { ...(this.value?.adapter || {}) },
      types: [
        { value: TYPES.POSTGRESQL, text: 'PostgreSQL' },
        { value: TYPES.MYSQL, text: 'MySQL' },
        { value: TYPES.MSSQL, text: 'Microsoft SQL Server' },
        { value: TYPES.ORACLE, text: 'Oracle' },
        { value: TYPES.DB2, text: 'IBM Db2' },
        { value: TYPES.MONGODB, text: 'MongoDB' },
        { value: TYPES.CSV, text: 'CSV / Flat File' },
        { value: TYPES.COBOL, text: 'COBOL / Mainframe' },
      ],
    };
  },
  computed: {
    fields(): object[] {
      if (!this.type) {
        return [];
      }

      return fields[this.type];
    },
    config(): object {
      const adapter = { ...this.adapter };
      if (adapter['port'] && parseInt(adapter['port'])) {
        adapter['port'] = parseInt(adapter['port']);
      } else {
        delete adapter['port'];
      }

      return {
        adapter,
        type: this.type,
      };
    },
  },
  methods: {
    label(value: string) {
      return startCase(value);
    },
    changed() {
      this.$emit('input', this.config);
    },
    handleKey(event) {
      if (event.keyCode === 13) {
        this.$emit('submit');
      }
    },
  },
  watch: {
    adapter: {
      deep: true,
      handler() {
        this.changed();
      },
    },
    type() {
      this.changed();
    },
    value(val, oldVal) {
      if (val.type != this.type || !isEqual(val.adapter, this.adapter)) {
        this.type = val.type;
        this.adapter = val.adapter;
      }
    },
    // config(val) {
    //   console.log('db form computed config chnageddd');
    // },
    // value(val) {
    //   console.log('value changed in db formmm, same?', val === this.value);
    // },
  },
});
</script>
